import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { fmtTime } from "./format";

const ROLE_BADGE = { admin: "bg-primary/15 text-primary", manager: "bg-emerald-500/15 text-emerald-600", attorney: "bg-blue-500/15 text-blue-500", staff: "bg-muted text-muted-foreground" };
const BLANK = { name: "", email: "", password: "", role: "staff", title: "", practice_group: "" };

function PresencePanel({ presence }) {
  return (
    <div className="border border-border bg-card rounded-sm p-4 mb-4" data-testid="presence-panel">
      <div className="flex items-center gap-2 mb-2">
        <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" /></span>
        <span className="text-sm font-semibold">Currently Online</span>
        <span className="text-xs text-muted-foreground">· signed in within {presence.window_minutes} min</span>
        <span className="ml-auto text-2xl font-condensed font-extrabold" data-testid="online-count">{presence.count}</span>
      </div>
      {presence.online.length === 0 ? <p className="text-xs text-muted-foreground">No one is online right now.</p> : (
        <div className="flex flex-wrap gap-2" data-testid="online-users">
          {presence.online.map((u) => (
            <span key={u.id} className="inline-flex items-center gap-1.5 text-xs bg-emerald-500/10 text-emerald-700 px-2.5 py-1 rounded-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />{u.name || u.email}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateUserDialog({ open, setOpen, onCreated }) {
  const [form, setForm] = useState(BLANK);
  const create = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("Name, email and password required");
    try { await api.post("/users", form); toast.success("User created"); setOpen(false); setForm(BLANK); onCreated(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not create"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="add-user-btn"><Plus className="h-4 w-4 mr-1.5" /> New User</Button></DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Create User Account</DialogTitle><DialogDescription className="sr-only">Create a user account.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Name</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label className="text-xs">Email</Label><Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label className="text-xs">Temporary Password</Label><Input data-testid="user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager (editor)</SelectItem>
                  <SelectItem value="attorney">Attorney (editor)</SelectItem>
                  <SelectItem value="staff">Staff (view)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Title</Label><Input data-testid="user-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
          </div>
        </div>
        <DialogFooter><Button data-testid="save-user-btn" onClick={create}>Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function UsersTab() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [presence, setPresence] = useState(null);
  const [open, setOpen] = useState(false);
  const loadUsers = useCallback(() => api.get("/users").then((r) => setUsers(r.data)).catch((e) => console.error("users load", e)), []);
  useEffect(() => { loadUsers(); api.get("/presence").then((r) => setPresence(r.data)).catch((e) => console.error("presence", e)); }, [loadUsers]);
  const remove = async (id) => { try { await api.delete(`/users/${id}`); loadUsers(); toast.success("Deleted"); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };

  return (
    <>
      {presence && <PresencePanel presence={presence} />}
      <div className="flex justify-end mb-4"><CreateUserDialog open={open} setOpen={setOpen} onCreated={loadUsers} /></div>
      <div className="border border-border rounded-sm overflow-hidden" data-testid="users-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/60"><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Title</TableHead><TableHead>Last Seen</TableHead><TableHead></TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid="user-row">
                <TableCell className="font-medium flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-primary" />{u.name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                <TableCell><span className={`text-[11px] px-1.5 py-0.5 rounded-sm uppercase font-semibold ${ROLE_BADGE[u.role] || "bg-muted"}`}>{u.role}</span></TableCell>
                <TableCell className="text-sm text-muted-foreground">{u.title}</TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{u.last_seen ? fmtTime(u.last_seen) : "—"}</TableCell>
                <TableCell>
                  {u.id !== user.id && <button data-testid="delete-user-btn" onClick={() => remove(u.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
